
import React from 'react';
import { useTranslation } from 'react-i18next';
import { activityService, DailyActivity } from '../../api/activityService';
import { useStatsRefresh } from '../../context/StatsRefreshContext';

// "today" / "yesterday" / "3 days ago" in the interface language. Falls back to
// the API's English label when talking to an API without days_ago.
export function formatDaysAgo(activity: Pick<DailyActivity, 'daysAgo' | 'period'>, lang: string): string {
    if (activity.daysAgo == null) return activity.period;
    try {
        const text = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' }).format(-activity.daysAgo, 'day');
        return text.charAt(0).toLocaleUpperCase(lang) + text.slice(1);
    } catch {
        return activity.period;
    }
}

const RecentActivity = () => {
    const { t, i18n } = useTranslation('statistics');
    const { overview, overviewLoading } = useStatsRefresh();
    const activities = activityService.mapActivityItems(overview?.activity_items);
    const loading = overviewLoading;

    const getActivityText = (activity: DailyActivity) => {
        if (activity.type === 'collection') {
            return activity.collectionName
                ? t('recent_activity.created_collection', { name: activity.collectionName })
                : t('recent_activity.created_collection', { name: '' });
        }

        const parts = [];
        if (activity.addedCount > 0) {
            parts.push(t('recent_activity.added_words', { count: activity.addedCount }));
        }
        if (activity.reviewedCount > 0) {
            parts.push(t('recent_activity.reviewed_words', { count: activity.reviewedCount }));
        }
        return parts.join(', ') || t('recent_activity.empty');
    };

    const getActivityDotClass = (activity: DailyActivity) => {
        if (activity.type === 'collection') return 'orange';
        if (activity.addedCount > 0) return 'green';
        return 'blue';
    };

    if (loading) {
        return (
            <div className="activity-panel">
                <h2 className="panel-title">{t('recent_activity.title')}</h2>
                <div>{t('recent_activity.loading')}</div>
            </div>
        );
    }

    return (
        <div>
            {/* Recent Activity */}
            <div className="activity-panel">
                <h2 className="panel-title">{t('recent_activity.title')}</h2>

                <div className="activity-list">
                    {activities.length === 0 ? (
                        <div className="activity-item">
                            <div className="activity-content">
                                <p className="activity-text">{t('recent_activity.empty')}</p>
                            </div>
                        </div>
                    ) : (
                        activities.map((activity) => (
                            <div key={activity.id} className="activity-item">
                                <div className={`activity-dot ${getActivityDotClass(activity)}`}></div>
                                <div className="activity-content">
                                    <div className="activity-header">
                                        <p className="activity-text">{getActivityText(activity)}</p>
                                        <span className="activity-time">{formatDaysAgo(activity, i18n.language)}</span>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default RecentActivity;
